import { useMemo } from 'react';
import { Checkbox, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { validateLicence, RIDES_LICENCE_TYPE, RIDES_RULE_CODES, type RidesPublicSettingsView, type RidesRuleCheck, type RidesLookupItem } from '@actschurch/ccms-sdk';
import { useRidesLookups, licenceCodeOptions, licenceCodeIdsFor, countryOptions } from '@/lib/lookups';
import { ruleCodeCopy } from '@/lib/copy';
import { parseApiDate, todayYmd, toYmd } from '@/lib/format';
import { ExpiryDateInput } from '@/components/ui/ExpiryDateInput';
import { useRidesSettings, allowedLicenceCodeList, DEFAULT_SETTINGS } from '@/lib/settings';

/** "B, EB and C1" (no serial comma). */
function listWithAnd(items: string[]): string {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0] ?? '';
}

export interface LicenceDraft {
  licenceTypeID: number;
  licenceCodeID: number | null;
  country: string;
  expiresAt: Date | null;
  inEnglish: boolean;
  hasIdp: boolean;
  /** Kept in memory only; sent once to verification/2/start, then cleared. */
  licenceNumber: string;
}

export const EMPTY_LICENCE: LicenceDraft = {
  licenceTypeID: RIDES_LICENCE_TYPE.SouthAfrican,
  licenceCodeID: null,
  country: 'ZAF',
  expiresAt: null,
  inEnglish: false,
  hasIdp: false,
  licenceNumber: '',
};

/** Rules the portal mirrors from settings (the SDK helper has no allowed-code parameter). */
export interface LicenceRuleOptions {
  /** Lookup ids of settings.allowedLicenceCodes; when given it replaces the SDK's built-in code set. */
  allowedCodeIds?: number[];
  /** settings.foreignLicenceNeedsEnglishOrIdp: false drops the English/IDP requirement. */
  foreignNeedsEnglishOrIdp?: boolean;
  /** settings.allowForeignLicence: false rejects foreign licences outright. */
  allowForeign?: boolean;
}

/**
 * `validateLicence` + the settings-driven rules. The SDK helper only knows the seeded six codes,
 * so the allowed-code check is re-applied here with the list the API gave us.
 */
export function checkLicence(d: LicenceDraft, opts: LicenceRuleOptions = {}): RidesRuleCheck {
  const base = validateLicence({
    licenceTypeId: d.licenceTypeID,
    licenceCodeId: d.licenceCodeID,
    expiresAt: d.expiresAt ? toYmd(d.expiresAt) : null,
    country: d.country,
    inEnglish: d.inEnglish,
    hasIdp: d.hasIdp,
  });
  let codes = [...base.codes];
  if (d.licenceTypeID === RIDES_LICENCE_TYPE.SouthAfrican && opts.allowedCodeIds) {
    codes = codes.filter((c) => c !== RIDES_RULE_CODES.LicenceCode);
    if (d.licenceCodeID == null || !opts.allowedCodeIds.includes(d.licenceCodeID)) codes.push(RIDES_RULE_CODES.LicenceCode);
  }
  if (d.licenceTypeID === RIDES_LICENCE_TYPE.Foreign) {
    if (opts.foreignNeedsEnglishOrIdp === false) codes = codes.filter((c) => c !== RIDES_RULE_CODES.ForeignLicenceNotUsable);
    if (opts.allowForeign === false && !codes.includes(RIDES_RULE_CODES.Format)) codes.push(RIDES_RULE_CODES.Format);
  }
  return { ...base, codes, valid: codes.length === 0 };
}

/** Rule options derived from the public settings + the licence code lookup. */
export function licenceRuleOptions(settings: RidesPublicSettingsView, licenceCodes: RidesLookupItem[] | undefined): LicenceRuleOptions {
  const ids = licenceCodeIdsFor(licenceCodes, allowedLicenceCodeList(settings));
  return {
    allowedCodeIds: ids.length ? ids : undefined,
    foreignNeedsEnglishOrIdp: settings.foreignLicenceNeedsEnglishOrIdp,
    allowForeign: settings.allowForeignLicence,
  };
}

/** Draft complete + passes the rules (settings-aware when `opts` is given). */
export function licenceDraftValid(d: LicenceDraft, opts: LicenceRuleOptions = {}): boolean {
  return checkLicence(d, opts).valid && d.licenceNumber.trim().length >= 4;
}

/** Convenience hook: the rule options for the current settings + lookups. */
export function useLicenceRuleOptions(): LicenceRuleOptions {
  const { settings } = useRidesSettings();
  const { data: lookups } = useRidesLookups();
  return useMemo(() => licenceRuleOptions(settings, lookups?.licenceCodes), [settings, lookups?.licenceCodes]);
}

/** Plain words for the SA licence codes (the lookup only carries the code). */
const CODE_WORDS: Record<string, string> = {
  B: 'cars, bakkies and minibuses up to 3 500 kg',
  EB: 'code B with a heavy trailer',
  C1: 'trucks from 3 500 to 16 000 kg',
  EC1: 'code C1 with a heavy trailer',
  C: 'trucks over 16 000 kg',
  EC: 'code C with a heavy trailer',
};

/** SA card / foreign licence form with live rule feedback. Codes, passport/foreign switches and IDP rule come from settings. */
export function LicenceForm({ value, onChange, disabled }: { value: LicenceDraft; onChange: (next: LicenceDraft) => void; disabled?: boolean }) {
  const { data: lookups } = useRidesLookups();
  const { settings } = useRidesSettings();
  const opts = useLicenceRuleOptions();
  const allowedCodes = allowedLicenceCodeList(settings);
  const allowForeign = settings.allowForeignLicence;
  const needsEnglishOrIdp = settings.foreignLicenceNeedsEnglishOrIdp;
  const sa = value.licenceTypeID === RIDES_LICENCE_TYPE.SouthAfrican || !allowForeign;
  const check = useMemo(() => checkLicence(value, opts), [value, opts]);
  const has = (code: string) => check.codes.includes(code);
  const set = (patch: Partial<LicenceDraft>) => onChange({ ...value, ...patch });
  const codeOptions = licenceCodeOptions(lookups?.licenceCodes, allowedCodes).map((o) => {
    const code = o.label.split(':')[0].trim().toUpperCase();
    return CODE_WORDS[code] && o.label.trim().toUpperCase() === code ? { ...o, label: `${code}: ${CODE_WORDS[code]}` } : o;
  });
  const codeHint = allowedCodes.length ? `Code ${allowedCodes[0]} or higher` : 'Code B or higher';
  const defaultCodes = allowedCodes.join(',') === DEFAULT_SETTINGS.allowedLicenceCodes;

  return (
    <Stack gap="sm">
      {allowForeign && (
        <div className="rides-seg" role="radiogroup" aria-label="Licence type">
          <button type="button" role="radio" className="rides-segbtn" aria-checked={sa} disabled={disabled} onClick={() => set({ licenceTypeID: RIDES_LICENCE_TYPE.SouthAfrican, country: 'ZAF' })}>
            SA licence card<small>{codeHint}</small>
          </button>
          <button type="button" role="radio" className="rides-segbtn" aria-checked={!sa} disabled={disabled} onClick={() => set({ licenceTypeID: RIDES_LICENCE_TYPE.Foreign, country: value.country === 'ZAF' ? '' : value.country })}>
            Foreign licence<small>{needsEnglishOrIdp ? 'In English or with an IDP' : 'From any country'}</small>
          </button>
        </div>
      )}

      {sa ? (
        <Select
          label="Licence code"
          placeholder="Choose the code on your card"
          data={codeOptions}
          value={value.licenceCodeID ? String(value.licenceCodeID) : null}
          onChange={(v) => set({ licenceCodeID: v ? Number(v) : null })}
          disabled={disabled}
          error={value.licenceCodeID && has(RIDES_RULE_CODES.LicenceCode) ? ruleCodeCopy(RIDES_RULE_CODES.LicenceCode, { allowedCodes }) : undefined}
          description={defaultCodes ? 'Most people have code B. It covers normal cars and minibuses up to 3 500 kg, so a Quantum is fine.' : `Carpool accepts code ${listWithAnd(allowedCodes)}.`}
        />
      ) : (
        <>
          <Select
            label="Issuing country"
            placeholder="Choose a country"
            data={countryOptions(lookups).filter((c) => c.value !== 'ZAF')}
            value={value.country || null}
            onChange={(v) => set({ country: v ?? '' })}
            searchable
            disabled={disabled}
            error={value.country === '' ? undefined : has(RIDES_RULE_CODES.CountryRequired) ? ruleCodeCopy(RIDES_RULE_CODES.CountryRequired) : undefined}
          />
          <Checkbox label="My licence is in English and has my photo" checked={value.inEnglish} onChange={(e) => set({ inEnglish: e.currentTarget.checked })} disabled={disabled} />
          <Checkbox label="I hold an International Driving Permit (IDP)" checked={value.hasIdp} onChange={(e) => set({ hasIdp: e.currentTarget.checked })} disabled={disabled} />
          {needsEnglishOrIdp && !value.inEnglish && !value.hasIdp && <Text size="xs" c="red">{ruleCodeCopy(RIDES_RULE_CODES.ForeignLicenceNotUsable)}</Text>}
        </>
      )}

      <TextInput
        label="Licence number"
        placeholder="As printed on the licence"
        autoComplete="off"
        value={value.licenceNumber}
        onChange={(e) => set({ licenceNumber: e.currentTarget.value.toUpperCase() })}
        disabled={disabled}
        description="We check the number. We don't keep it."
        rightSection={value.licenceNumber.trim().length >= 4 ? <IconCheck size={18} color="var(--rides-ok)" aria-label="Entered" /> : undefined}
      />
      <ExpiryDateInput
        label="Expiry date"
        value={value.expiresAt}
        onChange={(ymd) => set({ expiresAt: ymd ? parseApiDate(ymd) : null })}
        minDate={todayYmd()}
        disabled={disabled}
        error={value.expiresAt && has(RIDES_RULE_CODES.Expired) ? ruleCodeCopy(RIDES_RULE_CODES.Expired, { document: 'licence' }) : undefined}
        description={sa ? 'SA car licence cards are valid for 5 years.' : 'An IDP expires with the licence it accompanies.'}
      />
    </Stack>
  );
}
